/**
 * @mixin
 * this mixin is the base for any DataInputMenu functionality that relevant to the ParamType itself.
 * the default tabs of the dataInput are params only, serving optional methods to extend it
 *
 * any of the methods or computed written here will be available to the ParamType extending this mixin
 *
 * if working with RichTextBox, should be given 'ref="richEditor"' to the component
 *
 * Extended by [ParamTypeExtenderDataInputMenuMixin, InListParamTypeExtenderDataInputMenuMixin]
 */

import { mapActions } from 'vuex';

import ParamBaseDataInputMenuMixinService from './param-base-data-input-menu-mixin-service';
import { getDataInputMenuTab, THIS_FLOW_TAB_KEY } from '../../../../DataInput/DataInputMenu/helpers/data-input-menu.const';
import DataInputMenuService from '@/components/DataInput/DataInputMenu/data-input-menu-service';
import FormulasService from '@/components/common/RichTextBox/FormulaChip/formulas-service';
import { FORMULAS_TO_RICH_TEXT } from '@/components/common/RichTextBox/FormulaChip/formulas.const';
import { PARAGRAPH_CLOSE_TAG, PARAGRAPH_OPEN_TAG } from '@/components/common/RichTextBox/format-conversion-service.const';
import { setValueForParamKey, clearDataInputState } from '@/store/modules/dataInputMenu/actions';
import { autofillSuggestions } from '@/store/modules/selectedNode/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { isNullOrUndefinedOrEmpty, formatArray } from '@/utils/helpers';

const TAB_INDEX_AFTER_AUTOFILL = 1;

/**
 * For full functionality with RichTextEditor, should give ref="richEditor" to the component
 */
export default {
	mixins: [],
	computed: {
		...mapGettersShorthand({ autofillSuggestions }),
		curTab() {
			return this.$data._tabItems[this.$data._tab];
		},
		curTabCode() {
			return this.curTab?.tab;
		},
		allowMultipleSelection() {
			return this.param.multiple_select || this.param.type === 'optional_params_picker' || this.param.type === 'multiple_select';
		},
		dataInputMenuProps() {
			return {
				param: this.param,
				curTabCode: this.curTabCode,
				localValue: this.localValue,
				tabItems: this.$data._tabItems,
				tab: this.$data._tab,
				allowMultipleSelection: this.allowMultipleSelection,
			};
		},
		dataInputListeners() {
			return this.$data._dataInputListeners;
		},
		acceptsSingularValue() {
			return this.$data._replaceValueOnSelection;
		},
		disableCustomizable() {
			return this.$data._disableCustomMultipleSelectionSupport;
		},
		externalSourceReturnObject() {
			return this.param.source_return_full_object;
		},
		allowCustomValue() {
			return this.curTab?.custom;
		},
		showDataMenu() {
			return this.$data._showDataMenu;
		},
		isFocused() {
			return this.$data._isFocused;
		},
		didOpenBySlash() {
			return this.$data._didOpenBySlash;
		},
		autofillSuggestionsForParam() {
			return JSON.parse(JSON.stringify(this.autofillSuggestions[this.param.key] || []));
		},
	},
	methods: {
		...mapActions([setValueForParamKey.fullPath, clearDataInputState.fullPath]),
		// method to save localValue changes from DataInputMenu dynamic parameters
		setTab(val) {
			this.$data._tab = val;
		},
		updateValueFromMenu(value) {
			const hasRichEditor = this.hasRichEditor();
			const oldValueIsConst = !isNullOrUndefinedOrEmpty(this.localValueDisplay);
			const valueNotExists = !this.localValue;
			const needToSetSingleValue =
				(this.acceptsSingularValue && (!this.allowCustomValue || this.disableCustomizable)) ||
				ParamBaseDataInputMenuMixinService.isValueDifferentBetweenTwoDynamicColumns.call(this);
			const needToReplaceValue = valueNotExists || oldValueIsConst || needToSetSingleValue;

			if (needToReplaceValue || !hasRichEditor) {
				this.localValue = value;
			} else if (hasRichEditor) {
				let valueToUpdate = value;
				if (FormulasService.hasFormula(value)) {
					const { functionName } = FormulasService.getFormulaFunctionData(value);
					valueToUpdate = FORMULAS_TO_RICH_TEXT.includes(functionName) ? `${PARAGRAPH_OPEN_TAG}${value}${PARAGRAPH_CLOSE_TAG}` : value;
				}
				this.$refs.richEditor.addValue(`${valueToUpdate}`, this.didOpenBySlash);
			}

			this.localValueDisplay = '';

			if (this.acceptsSingularValue) {
				this.closeDataInputMenu();
			}
		},
		// method to save localValue changes from DataInputMenu constant values (such as: externalSource, selectOptions)
		updateConstantValueFromDataInputMenu(payload) {
			this.syncEditNodeAfterUpdate = true;
			const payloadIsArray = Array.isArray(payload);
			// Legacy we had support for .id as well.
			// Generate the display for an Array
			if (payloadIsArray) {
				this.localValueDisplay = formatArray(payload.map((entry) => entry.option));
				this.localValue = payload;
			} else {
				// Flag to support returning the whole object and not just value.

				this.localValue = this.externalSourceReturnObject ? payload : payload.value;
				this.localValueDisplay = payload.name || payload.option;

				// Dismiss menu only if we are not allowing to pick more than one
				this.closeDataInputMenu();
				this.onInputBlur();
			}
			this.syncEditNodeAfterUpdate = false;
		},
		hasRichEditor() {
			return !!this.$refs.richEditor;
		},
		onDataInputMenuClose() {
			this.closeDataInputMenu();
		},
		closeDataInputMenu() {
			this.$data._showDataMenu = false;
		},
		onInputBlur() {
			this.$data._isFocused = false;
		},
		onInputClick(dontOpenMenuOnClick) {
			if (this.$data._showDataMenu) {
				this.closeDataInputMenu();
			} else {
				const richEditorValue = this.$refs.richEditor?.value;
				const isRichEditorEmpty = !richEditorValue || richEditorValue === '';
				const avoidMenuOpenOnClick = dontOpenMenuOnClick && !isRichEditorEmpty;
				!avoidMenuOpenOnClick && this.openDataInputMenu();
			}
		},
		onSlashOpen() {
			this.$data._carretCoordinatesOnSlashOpen = DataInputMenuService.getCursorPosition();
			this.$data._didOpenBySlash = true;
			this.openDataInputMenu();
		},
		onContentUpdate(newChar) {
			if (newChar === '/') {
				this.onSlashOpen();
			} else {
				if (this.showDataMenu) {
					this.closeDataInputMenu();
				}
			}
		},
		onInputFocus() {
			this.$data._isFocused = true;
		},
		openDataInputMenu() {
			this.$data._showDataMenu = true;
		},
		// method to give options to extend the data input tab items
		addTabItem(key, title, isConst, custom = false, pushToStart = true) {
			const tab = getDataInputMenuTab(key, title, isConst, custom);
			pushToStart ? this.$data._tabItems.unshift(tab) : this.$data._tabItems.push(tab);
		},
		// method to give options to extend the data input listeners
		addDataInputMenuListener(event, method) {
			this.$set(this.$data._dataInputListeners, event, method);
		},
		// method to give options to extend the data input props
		setDataInputMenuProp(key, value) {
			ParamBaseDataInputMenuMixinService.setDataInputStateValue.call(this, key, value);
		},
		// to be used when we want to force user to replace the whole flag
		setReplaceValueOnSelection(value) {
			this.$set(this.$data, '_replaceValueOnSelection', value);
		},
		setDisableCustomMultipleSelectionSupport(value) {
			this.$set(this.$data, '_disableCustomMultipleSelectionSupport', value);
		},
		showMenu() {
			this.$refs['data-input-menu']?.showMenu({
				targetElement: this.inputElement,
				didOpenBySlash: this.didOpenBySlash,
				carretCoordinatesOnSlashOpen: this.$data._carretCoordinatesOnSlashOpen,
			});
		},
		hideMenu() {
			this.$refs['data-input-menu']?.hideMenu();

			if (this.$data._didOpenBySlash) {
				this.$data._didOpenBySlash = false;
				this.$refs.richEditor?.focus(); //after menu closes, refocus on input TODO: Check with product its necessary
			}
		},
	},
	mounted() {
		ParamBaseDataInputMenuMixinService.addDataInputMenuListeners.call(this);
		// add callback to decide rather we should hide real value of localValue from user
		ParamBaseDataInputMenuMixinService.addHideRealValueCallback.call(this);
		// add middleware to execute before we set localValue
		ParamBaseDataInputMenuMixinService.addSetLocalValueBeforeMiddleware.call(this);
		/**
		 * todo - add logic here when we should accept only one value - should be in mixin as it shared for all ParamTypes
		 *
		 * thought - maybe it should be on specific paramTypes responsibility? or at least for now? (such as ListSourceParamType & ConditionSourceParamType)
		 */
		const acceptsOneValue = false;
		if (acceptsOneValue) {
			this.setReplaceValueOnSelection(true);
		}
		this.$nextTick(() => {
			ParamBaseDataInputMenuMixinService.decideCurrentTab.call(this);
		});
	},
	beforeDestroy() {
		ParamBaseDataInputMenuMixinService.clearDataInputState.call(this);
	},
	watch: {
		dataInputMenuProps: {
			handler() {
				ParamBaseDataInputMenuMixinService.setDataInputMenuProps.call(this);
			},
			deep: true,
			immediate: true,
		},
		showDataMenu: {
			handler(newV) {
				if (newV) {
					this.showMenu();
				} else {
					this.hideMenu();
				}
			},
			immediate: true,
		},
		inputElement: {
			handler() {
				if (this.showDataMenu) {
					this.showMenu();
				}
			},
		},
		autofillSuggestionsForParam: {
			/**
			 * Change the tab to "map data" when we have suggestions
			 */
			handler(newValue, oldValue) {
				const isInThisFlowTab = this.curTabCode === THIS_FLOW_TAB_KEY;
				if (newValue.length > oldValue.length && !isInThisFlowTab) {
					this.setTab(TAB_INDEX_AFTER_AUTOFILL);
				}
			},
			deep: true,
		},
	},
	data() {
		return {
			_tab: 0,
			_showDataMenu: false,
			// the tabs provided to the data-input component
			_tabItems: [],
			_dataInputListeners: {},
			/**
			 * private flags to use when we want to force user to replace the whole value
			 * should be set by setReplaceValueOnSelection / setDisableCustomMultipleSelectionSupport
			 */
			_replaceValueOnSelection: false,
			_disableCustomMultipleSelectionSupport: false,
			_isFocused: false, // synced with the focus/blur state of the RichTextBox inputs element
			_didOpenBySlash: false,
			_carretCoordinatesOnSlashOpen: null,
		};
	},
};
